import React, { useState } from "react";
import { withKey } from "../../data/apiReference";
import { useDemoKey } from "../../lib/demoKey";

// Stable anchor id from heading text (shared by sidebars / TOCs).
export const slugify = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Smooth-scroll to an element, accounting for the fixed navbar.
export const scrollToId = (id, offset = 110) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
};

const CopyIcon = ({ done }) =>
  done ? (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="7" y="7" width="10" height="10" rx="2" />
      <path d="M13 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" strokeLinecap="round" />
    </svg>
  );

// Copy-to-clipboard button used by code blocks and slug chips.
export const CopyButton = ({ text, className = "", label = "Copy" }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1400);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={label}
      title={label}
      className={`inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-xs font-medium text-gray-300 transition-colors hover:border-primary/50 hover:text-primary ${className}`}
    >
      <CopyIcon done={done} />
      {done ? "Copied" : label}
    </button>
  );
};

// Dark code block — the key token is substituted at render time.
export const CodeBlock = ({ code, label, response, className = "" }) => {
  const { key } = useDemoKey();
  const text = withKey(code, key);
  return (
    <div className={`doc-reveal group relative mb-5 overflow-hidden rounded-xl border border-white/10 bg-[#0A0A14] ${className}`}>
      {(label || response) && (
        <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-2">
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-gray-500">{label}</span>
          <CopyButton text={text} />
        </div>
      )}
      {!label && !response && (
        <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">
          <CopyButton text={text} />
        </div>
      )}
      <pre className="m-0 overflow-x-auto p-4 font-mono text-[13px] leading-relaxed text-gray-200">
        <code>{text}</code>
      </pre>
      {response && (
        <>
          <div className="flex items-center border-t border-white/[0.06] px-4 py-2">
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-primary/80">Response</span>
          </div>
          <pre className="m-0 overflow-x-auto bg-white/[0.02] p-4 font-mono text-[13px] leading-relaxed text-primary-100">
            <code>{withKey(response, key)}</code>
          </pre>
        </>
      )}
    </div>
  );
};

// Inline monospace chip (slugs, headers, URLs).
export const Mono = ({ children, accent }) => (
  <code
    className="rounded-md border border-white/10 bg-white/[0.05] px-1.5 py-0.5 font-mono text-[0.85em]"
    style={accent ? { color: accent } : undefined}
  >
    {children}
  </code>
);

export const DocTable = ({ headers = [], rows = [], accent = "#99DFAF", mono = false, className = "" }) => {
  const { key } = useDemoKey();
  return (
    <div className={`doc-reveal mb-6 overflow-x-auto rounded-xl border border-white/10 ${className}`}>
      <table className="w-full text-left text-sm">
        {headers.length > 0 && (
          <thead>
            <tr style={{ backgroundColor: `${accent}1A` }}>
              {headers.map((h, i) => (
                <th key={i} className="px-4 py-2.5 font-semibold text-white">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => {
            const cells = Array.isArray(row) ? row : row.cells;
            return (
              <tr key={i} className="border-t border-white/10 transition-colors hover:bg-white/[0.02]">
                {cells.map((cell, j) => (
                  <td
                    key={j}
                    className={`px-4 py-2.5 align-top text-gray-300 ${j === 0 && mono ? "whitespace-nowrap font-mono text-[13px] text-white" : ""}`}
                  >
                    {withKey(cell, key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const Callout = ({ title, children, accent = "#99DFAF", tone = "info", className = "" }) => {
  const color = tone === "warn" ? "#F5C46B" : accent;
  return (
    <div
      className={`doc-reveal mb-6 rounded-r-xl border-l-4 p-4 ${className}`}
      style={{ borderColor: color, backgroundColor: `${color}12` }}
    >
      {title && <p className="m-0 mb-1 text-sm font-semibold text-white">{title}</p>}
      <div className="m-0 text-sm leading-relaxed text-gray-300">{children}</div>
    </div>
  );
};

// Key/value card — used for endpoints & headers.
export const KVCard = ({ title, pill, rows = [], accent = "#99DFAF", className = "" }) => {
  const { key } = useDemoKey();
  return (
    <div className={`doc-reveal rounded-2xl border border-white/10 bg-white/[0.03] p-5 ${className}`}>
      {(pill || title) && (
        <div className="mb-3 flex items-center gap-2">
          {pill && (
            <span
              className="rounded-md px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: `${accent}26`, color: accent }}
            >
              {pill}
            </span>
          )}
          {title && <span className="text-sm font-semibold text-white">{title}</span>}
        </div>
      )}
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 text-sm">
        {rows.map(([k, v], i) => (
          <React.Fragment key={i}>
            <dt className="text-gray-500">{k}</dt>
            <dd className="m-0 break-all font-mono text-[13px] text-gray-200">{withKey(v, key)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

export const SectionHeading = ({ id, eyebrow, title, n, accent = "#99DFAF", children }) => (
  <div id={id} className="doc-reveal mb-6 mt-16 scroll-mt-28 border-b border-white/10 pb-4 first:mt-0">
    <div className="flex items-end justify-between gap-4">
      <div>
        {eyebrow && (
          <p className="m-0 mb-1 text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: accent }}>
            {eyebrow}
          </p>
        )}
        <h2 className="m-0 text-2xl font-bold text-white md:text-3xl">{title}</h2>
      </div>
      {n && <span className="font-mono text-sm text-gray-600">{n}</span>}
    </div>
    {children && <p className="mb-0 mt-3 max-w-3xl text-gray-400">{children}</p>}
  </div>
);

// Renders one article-style content block (same schema as the Articles feature).
export const Block = ({ block, accent = "#99DFAF" }) => {
  switch (block.type) {
    case "heading":
      return (
        <h2 id={slugify(block.text)} className="doc-reveal mb-4 mt-14 scroll-mt-28 border-b border-white/10 pb-3 text-2xl font-bold text-white md:text-3xl">
          {block.text}
        </h2>
      );
    case "subheading":
      return (
        <h3 id={slugify(block.text)} className="doc-reveal mb-3 mt-8 scroll-mt-28 text-lg font-bold text-white md:text-xl">
          {block.text}
        </h3>
      );
    case "paragraph":
      return <p className="doc-reveal mb-5 leading-relaxed text-gray-300">{block.text}</p>;
    case "code":
      return <CodeBlock code={block.text} />;
    case "table":
      return <DocTable headers={block.headers} rows={block.rows} accent={accent} mono />;
    case "list":
      return (
        <ul className="doc-reveal mb-6 list-outside list-disc space-y-2 pl-6 marker:[color:var(--accent)]">
          {block.items.map((item, i) => (
            <li key={i} className="leading-relaxed text-gray-300">
              {item}
            </li>
          ))}
        </ul>
      );
    case "callout":
      return <Callout accent={accent}>{block.text}</Callout>;
    case "image":
      return (
        <figure className="doc-reveal mb-8">
          <img src={block.url} alt={block.caption || ""} className="w-full rounded-2xl border border-white/10" />
          {block.caption && <figcaption className="mt-2 text-center text-sm text-gray-500">{block.caption}</figcaption>}
        </figure>
      );
    default:
      return null;
  }
};
